import React from 'react';
import { Field } from 'formik';
import { tv } from 'tailwind-variants';
import { FormFieldWrapper } from './FormFieldWrapper';
import { CustomForm } from '.generated/Feature.Irwinmitchell.model';
import useDictionary from 'lib/hooks/useDictionary';

const formFieldStyles = tv({
  base: [
    'w-full',
    'px-spacing-spacing-16',
    'py-spacing-spacing-12',
    'border',
    'border-color-input-border',
    'rounded-[8px]',
    'bg-color-grayscale-white',
    'placeholder:text-color-text-text-secondary',
    'text-color-text-text',
    'hover:border-color-input-border-hover',
    'font-typography-font-family-body',
    'text-typography-font-size-body-medium',
    'placeholder:italic',
    'font-normal',
    'disabled:text-color-text-text-disabled',
    'disabled:cursor-not-allowed',
    'focus:outline-none',
    'focus:ring-1',
    'focus:border-color-input-border-focused',
    'focus:ring-color-input-border-focused',
    'focus:ring-shadow-[0_0_0_1px_var(--color-core-color-4-SkyBlue)]',
    'min-h-[120px]',
    'resize-vertical',
  ],
  variants: {
    state: {
      default: '',
      error: [
        'border-color-text-text-critical',
        'focus:border-color-text-text-error',
        'bg-color-fill-error-light',
      ],
      disabled: ['bg-color-grayscale-gray-100', 'cursor-not-allowed'],
    },
  },
  defaultVariants: {
    state: 'default',
  },
});

const characterCountStyles = tv({
  base: [
    'text-color-text-text',
    'font-typography-font-family-body',
    'text-typography-font-size-body-medium',
    'font-normal',
    'leading-typography-line-height-body-medium',
  ],
});

interface TextareaFieldProps {
  field: CustomForm.FormField_Item;
  hasError?: boolean;
  isTouched?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  errors?: Record<string, string | undefined>;
  touched?: Record<string, boolean | undefined>;
  hideLabel?: boolean;
  isStandalone?: boolean;
}

export const TextareaField: React.FC<TextareaFieldProps> = ({
  field,
  hasError = false,
  value,
  onChange,
  onBlur,
  errors,
  touched,
  hideLabel,
  isStandalone = false,
}) => {
  const { getDictionaryValue } = useDictionary();
  const characterRemainingText =
    getDictionaryValue('CharactersRemainingText') || 'characters remaining';

  const fieldConfig = field.fields;
  const fieldName = fieldConfig?.fieldName?.value || fieldConfig?.fieldId?.value;
  const label = fieldConfig?.label?.value || fieldName || '';
  const helpText = fieldConfig?.helpText?.value;
  const disabled = fieldConfig?.disabled?.value;
  const required = fieldConfig?.required?.value;

  // Ensure characterLimit is a valid number (undefined if not set = hide counter)
  const rawLimit = fieldConfig?.characterLimit?.value;
  const characterLimit =
    rawLimit && typeof rawLimit === 'number' && rawLimit > 0 ? rawLimit : undefined;

  const placeholder = fieldConfig?.placeholderText?.value;

  if (!fieldName) return null;

  const fieldState = disabled ? 'disabled' : hasError ? 'error' : 'default';

  const commonFieldProps = {
    name: fieldName,
    id: fieldName,
    disabled,
    className: formFieldStyles({ state: fieldState }),
    'data-automation': `textareaField-${fieldName}`,
  };

  const currentValue = isStandalone ? (value ?? '') : undefined;
  const currentLength = currentValue?.length || 0;
  const remainingChars = characterLimit ? characterLimit - currentLength : null;

  return (
    <FormFieldWrapper
      key={fieldName}
      fieldName={fieldName}
      label={label}
      helpText={helpText}
      isDisabled={disabled}
      hasError={hasError}
      isRequired={required}
      errors={errors}
      touched={touched}
      hideLabel={hideLabel}
    >
      {isStandalone ? (
        <>
          <textarea
            {...commonFieldProps}
            value={currentValue}
            onChange={(e) => onChange?.(e.target.value)}
            onBlur={onBlur}
            placeholder={placeholder}
            rows={4}
            aria-required={required}
          />
          {characterLimit && remainingChars !== null && (
            <div className={characterCountStyles()}>
              {remainingChars} {characterRemainingText}
            </div>
          )}
        </>
      ) : (
        <Field name={fieldName}>
          {({ field: formikField }: { field: { value: string } }) => {
            const formikLength = formikField.value?.length || 0;
            const formikRemainingChars = characterLimit ? characterLimit - formikLength : null;

            return (
              <>
                <textarea
                  {...formikField}
                  {...commonFieldProps}
                  placeholder={placeholder}
                  rows={4}
                  aria-required={required}
                />
                {characterLimit && formikRemainingChars !== null && (
                  <div
                    className={`${characterCountStyles()} ${characterLimit < formikLength ? 'text-color-text-text-critical' : ''}`}
                  >
                    {formikRemainingChars} {characterRemainingText}
                  </div>
                )}
              </>
            );
          }}
        </Field>
      )}
    </FormFieldWrapper>
  );
};

export default TextareaField;
